import {
  PaginationAbstractService,
  PaginationRequestOptions,
} from '@abstract-services/pagination/1.0/pagination.abstract-service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlSerializer } from '@angular/router';
import { ApiResponseObject } from '@interfaces/api-response/1.0';
import { EMPTY, Observable } from 'rxjs';
import { expand, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PaginationService extends PaginationAbstractService {
  MAX_PAGE_SIZE = 100;

  constructor(
    private http: HttpClient,
    private urlSerializer: UrlSerializer,
  ) {
    super();
  }

  /**
   * Fetches the page of data from the given URL
   * @param url - url that implements paging
   * @param options - options for the http request
   */
  fetchPage<T extends ApiResponseObject>(
    url: string,
    options: PaginationRequestOptions = {},
  ): Observable<T> {
    const urlTree = this.urlSerializer.parse(url);
    const effectiveSize = options.pageSize ?? this.MAX_PAGE_SIZE;

    options.params = options.params || {};

    // Add size param if not already in URL
    if (!urlTree.queryParamMap.get('size')) {
      options.params = { ...(options.params || {}), size: effectiveSize.toString() };
    }

    return this.http
      .get<T>(url, {
        headers: options.headers,
        params: options.params,
      })
      .pipe(
        map((response: T) => {
          return response;
        }),
      );
  }

  /**
   * Fetches the given URL and keeps fetching the next page of data until there are no more.
   * Returns an observable that emits the next slice of data on each request
   *
   * @param url - url that implements paging
   * @param options - options for the http request
   * @return Observable
   */
  fetchAllPages<T extends ApiResponseObject>(
    url: string,
    options: PaginationRequestOptions = {},
  ): Observable<T> {
    const effectiveSize = this.resolveEffectivePageSize(url, options);

    return this.fetchPage(url, options).pipe(
      map((response: T) => {
        return response;
      }),
      expand((x: T) => {
        if (x.hasOwnProperty('nextStart')) {
          return this.getNextPageEnforcingSize<T>(
            x._links.next.href,
            effectiveSize,
            options.headers,
          ).pipe(
            map((response: T) => {
              return response;
            }),
          );
        } else if (x.hasOwnProperty('nextPage')) {
          const urlTree = this.urlSerializer.parse(url);
          const params: Record<string, string> = {
            ...(options.params || {}),
            page: String(x['nextPage']),
          };

          // Keep size on the original URL when present so HttpClient does not send size twice
          if (!urlTree.queryParamMap.get('size')) {
            params.size = effectiveSize.toString();
          } else {
            delete params.size;
          }

          options.params = params;
          return this.fetchPage(url, options).pipe(
            map((response: T) => {
              return response;
            }),
          );
        } else {
          return EMPTY;
        }
      }),
    );
  }

  /**
   * Follows a HATEOAS next link while forcing `size` so the server cannot return size=0 or a shrinking page.
   */
  private getNextPageEnforcingSize<T>(
    href: string,
    effectiveSize: number,
    headers?: HttpHeaders,
  ): Observable<T> {
    const url = this.enforceQueryParamSize(href, effectiveSize);
    const reqOptions: { headers?: HttpHeaders } = {};
    if (headers) {
      reqOptions.headers = headers;
    }
    return this.http.get<T>(url, reqOptions);
  }

  /**
   * Page size for the whole chain: explicit option, else `size` on the first URL (e.g. pos-items?size=500),
   * else default max. Ensures HATEOAS next requests match the first page when callers put size only on the URL.
   */
  private resolveEffectivePageSize(url: string, options: PaginationRequestOptions): number {
    if (options.pageSize != null) {
      return options.pageSize;
    }
    const fromUrl = this.urlSerializer.parse(url).queryParamMap.get('size');
    if (fromUrl != null) {
      const n = Number(fromUrl);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
    return this.MAX_PAGE_SIZE;
  }

  private enforceQueryParamSize(href: string, effectiveSize: number): string {
    const sizeStr = String(effectiveSize);
    if (/^https?:\/\//i.test(href)) {
      const u = new URL(href);
      u.searchParams.set('size', sizeStr);
      return u.toString();
    }
    const tree = this.urlSerializer.parse(href);
    tree.queryParams = { ...tree.queryParams, size: sizeStr };
    return this.urlSerializer.serialize(tree);
  }
}
